import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import useTranslation from '@src/hooks/useTranslation';

import { formatTimeFloor } from '@src/utils/time';

const TimerTitle = styled.div`
	margin-bottom: 4px;
	font-size: 14px;
`;

const TimeUnit = styled.span`
	font-size: ${props => props.unitSize}px;
	color: ${props => props.color || '#101010'};

	&:not(:first-child) {
		margin-left: 8px;
	}
`;

const TimeValue = styled.span`
	margin-right: 4px;
	font-size: ${props => props.numberSize}px;
	font-weight: ${props => props.fontWeight || 500};
`;

interface ITimer {
	className?: string;
	timerTitle?: string;
	endTime: number;
	fontSize?: {
		numberSize?: number;
		unitSize?: number;
	};
	fontWeight?: number;
	color?: string;
}

const Timer = ({ className, timerTitle, endTime, fontSize, fontWeight, color }: ITimer): JSX.Element => {
	const { t } = useTranslation();
	const remainingSeconds = endTime - dayjs().valueOf();
	const [milliseconds, setＭilliseconds] = useState(remainingSeconds);
	const time = formatTimeFloor(milliseconds);
	const numberSize = fontSize?.numberSize || 28;
	const unitSize = fontSize?.unitSize || 14;

	useEffect(() => {
		const interval = setInterval(() => {
			if (milliseconds <= 0) {
				clearInterval(interval);
			} else {
				setＭilliseconds(seconds => seconds - 1000);
			}
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	});

	return (
		<div className={className}>
			{timerTitle && (<TimerTitle>{timerTitle}</TimerTitle>)}
			<div className="timer-content">
				<TimeUnit unitSize={unitSize} color={color}>
					<TimeValue numberSize={numberSize} fontWeight={fontWeight}>{time.days}</TimeValue>
					{t('time:day')}
				</TimeUnit>
				<TimeUnit unitSize={unitSize} color={color}>
					<TimeValue numberSize={numberSize} fontWeight={fontWeight}>{time.hours}</TimeValue>
					{t('time:hour')}
				</TimeUnit>
				<TimeUnit unitSize={unitSize} color={color}>
					<TimeValue numberSize={numberSize} fontWeight={fontWeight}>{time.minutes}</TimeValue>
					{t('time:minute')}
				</TimeUnit>
				<TimeUnit unitSize={unitSize} color={color}>
					<TimeValue numberSize={numberSize} fontWeight={fontWeight}>{time.seconds}</TimeValue>
					{t('time:second')}
				</TimeUnit>
			</div>
		</div>
	);
};

export default Timer;
